import { watchPreviewRoot } from './editor-preview-root.js';

// Draws the Recommended-card "spinning rays" ring directly to a
// <canvas class="event-card__glow-canvas"> instead of animating it with
// CSS (assets/src/scss/components/_event-card-recommended-glow.scss's own
// 'rays'/'breathe' modes) -- see that file's big doc comment for the full
// research trail. Short version: ANY CSS `animation` pays a per-frame
// main-thread cost baked into the Web Animations model itself (keeping
// the element's computed style in sync with the compositor), regardless
// of which property or delivery mechanism drives it. Canvas isn't part of
// that model at all -- painting to it is just pixel drawing -- so it
// doesn't pay that tax.
//
// Two further choices keep this cheap on top of just "being canvas":
// - Redraws are triggered by `setInterval` at REDRAW_INTERVAL_MS, not by
//   polling every requestAnimationFrame tick and skipping most of them.
//   Measured directly: an rAF-driven poll that only actually redraws every
//   Nth tick still costs roughly the same regardless of N, because the
//   poll itself runs at a fixed 60/s and that alone has a real cost --
//   setInterval sidesteps the poll entirely.
// - The full rotation only needs to look smooth at its own (slow) pace,
//   so REDRAW_INTERVAL_MS trades a few degrees of angular step for a
//   meaningfully lower redraw rate -- ~15fps here (2.4 degrees/redraw over
//   the 10s rotation), which reads as smooth for a slow ambient sweep at a
//   fraction of a 60fps rAF loop's cost.
//
// Ring shape: stroke(), not clip()+fill(). An earlier version filled the
// whole canvas with the dark base color, clipped to a ring band built from
// two nested rounded-rect Path2D subpaths combined via the 'evenodd' fill
// rule (matching how the CSS mask/clip-path versions punch their own
// ring-shaped hole). That construction turned out fragile in the field --
// reported as a solid dark rectangle covering the card's own image on some
// real devices (the "hole" not resolving, leaving the full-canvas fill
// visible) and as bright bloom/flare artifacts right at the rounded
// corners (the two subpaths' radii not lining up exactly at the arc
// joins). Stroking the SAME single rounded-rect path directly with a
// `lineWidth` sidesteps both: no fill/clip/evenodd interaction to get
// wrong, and a stroke's width is inherently uniform all the way around a
// path including its corners, unlike two independently-built nested paths
// that have to agree by construction.
//
// The rotating rays were originally drawn with a moving
// createConicGradient() as strokeStyle over this same stroke -- its own
// `startAngle` parameter carried the rotation for free, no context
// rotation needed. That coloring is by ANGLE FROM THE CARD'S CENTER,
// though, not by distance travelled along the ring itself, and for a tall
// card with a small corner radius those don't track each other: a fixed
// range of center-angles covers much less physical distance around a
// tight corner than the same angle range covers along a long straight
// edge. A ray band that reads as a normal moving highlight on the edges
// compresses into a small, disproportionately concentrated bright spot
// right at each corner -- confirmed in the field (not reproducible in
// headless testing) as an out-of-place-looking flare stuck to the corner,
// distinct in character from the rest of the ring's motion, even though
// it never actually left the card's bounds.
//
// colorAt()/buildArcLengthPoints() replace the conic gradient with
// coloring by ARC-LENGTH position along the ring (0 at the path's own
// start, back to 1 all the way around), so the same RAY_STOPS data
// produces a band of constant physical width everywhere on the ring,
// corners included. The ring is walked as the same 8-segment structure
// (4 straight edges, 4 quarter-circle corners) the base stroke's
// arcTo()-built path traces, sampled densely enough (RAY_SEGMENT_COUNT)
// that each short straight-line facet's deviation from the true corner
// curve is a small fraction of a device pixel -- visually indistinguishable
// from a true arc while staying cheap to draw (draw() skips any segment
// that interpolates to ~fully transparent, which most of the ring is at
// any given moment).
//
// Viewport pausing follows the same rationale as
// modules/swiper-viewport-autoplay.js and
// modules/recommended-glow-viewport.js: a display:none card (events-ajax.js's
// "load more" pagination) reports an all-zero rect, which is guarded
// against the same way. Unlike those two, there's no IntersectionObserver
// here -- the redraw tick already runs at a slow, cheap fixed interval, so
// a plain geometry check inside that same tick is simpler than wiring up
// a second, independent observation mechanism for the same decision.
const REDRAW_INTERVAL_MS = 67; // ~15fps
const ROTATION_PERIOD_MS = 10000; // matches 'rays'/'breathe' CSS mode timing
const VIEWPORT_MARGIN_PX = 200;
// An earlier version tried to make the ring "reach past" the card's true
// edge by both shifting the path's rect inward/outward (inset) AND growing
// its corner radius by the same amount, on the reasoning that this keeps
// the shape self-consistent as it grows. It does -- but self-consistent
// isn't the goal; matching the ONE true, fixed curve the card is actually
// clipped to (canvas's own border-radius, cardRadius, unmodified) is. A
// rounded rect's corner arc is centered at (x + r, y + r); shifting x/y
// while also changing r moves that center away from the card's true,
// fixed corner center, so the drawn arc traces a DIFFERENT circle than the
// one the card is really clipped to. Wherever that real clip then cuts
// into the wrong-shaped arc, the visible edge reads as the ring's
// rounding not matching the card's -- confirmed as the actual cause after
// checking whether Canvas arcTo()/arc() and CSS border-radius render
// visibly different curves at all (they don't: Chromium approximates both
// with the same ~0.5523 cubic-Bezier circle constant, off by a fraction of
// a percent -- not something that shows up at a 25px radius).
//
// Ring width is now the only knob (read per-instance below, not a fixed
// constant -- see getRingWidthPx()). The path itself always traces the
// exact card curve (x=0, y=0, r=cardRadius in resize() below, no inset, no
// radius offset), so its center and radius are identical to the true CSS
// clip's. The stroke straddles that path (half its width in, half out) --
// the "out" half naturally reaches past the true edge and gets trimmed by
// the CSS clip along that SAME curve, so the cut is seamless. To reach
// further out (or show more ring at all), grow the ring width -- the part
// that ends up visible is roughly ringWidthPx / 2 (the inward half stays
// inside the clip too, so it's part of what's visible, not just the
// outward half).
const DEFAULT_RING_WIDTH_PX = 4;

// Admin-configurable per block instance (blocks/upcoming-events/render.php's
// own "Highlight Ring Width - Canvas" field, --glow-canvas-ring-width custom
// property cascaded from .events-grid down onto every card, including this
// canvas) -- read once per instance in createInstance() below rather than a
// single site-wide constant, so two Upcoming Events blocks on the same page
// can each carry their own admin-configured size.
const getRingWidthPx = (canvas) => {
    const raw = parseFloat(getComputedStyle(canvas).getPropertyValue('--glow-canvas-ring-width'));
    return Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_RING_WIDTH_PX;
};

const MAX_DEVICE_PIXEL_RATIO = 2; // caps canvas backing-store size on high-DPI screens
const RAY_SEGMENT_COUNT = 180; // arc-length samples around the ring -- see the doc comment above for why

const RAY_STOPS = [
    [0, 'transparent'],
    [10 / 360, 'rgba(251, 241, 164, 1)'],
    [25 / 360, 'transparent'],
    [60 / 360, 'transparent'],
    [70 / 360, 'rgba(198, 176, 110, 1)'],
    [85 / 360, 'transparent'],
    [150 / 360, 'transparent'],
    [160 / 360, 'rgba(251, 241, 164, 1)'],
    [175 / 360, 'transparent'],
    [240 / 360, 'transparent'],
    [250 / 360, 'rgba(198, 176, 110, 1)'],
    [265 / 360, 'transparent'],
    [1, 'transparent'],
];

const roundRectPath = (x, y, w, h, r) => {
    const path = new Path2D();
    const radius = Math.max(0, Math.min(r, w / 2, h / 2));
    path.moveTo(x + radius, y);
    path.arcTo(x + w, y, x + w, y + h, radius);
    path.arcTo(x + w, y + h, x, y + h, radius);
    path.arcTo(x, y + h, x, y, radius);
    path.arcTo(x, y, x + w, y, radius);
    path.closePath();
    return path;
};

const parseColor = (str) => {
    if (str === 'transparent') {
        return { r: 0, g: 0, b: 0, a: 0 };
    }
    const parts = str
        .slice(str.indexOf('(') + 1, str.indexOf(')'))
        .split(',')
        .map((v) => parseFloat(v));
    return { r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 };
};

// RAY_STOPS's offsets are reused as arc-length fractions (0..1 all the way
// around the ring) instead of the conic-gradient angle fractions they were
// originally written for -- see the module doc comment for why. Parsed
// once at module load since the stop colors themselves never change.
const RAY_STOPS_PARSED = RAY_STOPS.map(([offset, color]) => [offset, parseColor(color)]);

const colorAt = (t) => {
    const wrapped = ((t % 1) + 1) % 1;
    let i = 0;
    while (i < RAY_STOPS_PARSED.length - 1 && RAY_STOPS_PARSED[i + 1][0] < wrapped) {
        i += 1;
    }
    const [o1, c1] = RAY_STOPS_PARSED[i];
    const [o2, c2] = RAY_STOPS_PARSED[Math.min(i + 1, RAY_STOPS_PARSED.length - 1)];
    const span = o2 - o1;
    const frac = span > 0 ? Math.min(1, Math.max(0, (wrapped - o1) / span)) : 0;
    return {
        r: c1.r + (c2.r - c1.r) * frac,
        g: c1.g + (c2.g - c1.g) * frac,
        b: c1.b + (c2.b - c1.b) * frac,
        a: c1.a + (c2.a - c1.a) * frac,
    };
};

// Samples `count` + 1 points evenly by arc-length around the same
// straight-edges/quarter-circle-corners shape roundRectPath() strokes,
// each tagged with its fractional distance travelled (0 at the path's own
// start point, back to 1 all the way around) -- see the module doc
// comment for why this replaces angle-from-center coloring.
const buildArcLengthPoints = (x, y, w, h, r, count) => {
    const radius = Math.max(0, Math.min(r, w / 2, h / 2));
    const straightW = w - 2 * radius;
    const straightH = h - 2 * radius;
    const cornerLen = (Math.PI / 2) * radius;
    const segments = [
        { kind: 'line', len: straightW, from: [x + radius, y], to: [x + w - radius, y] },
        {
            kind: 'arc',
            len: cornerLen,
            cx: x + w - radius,
            cy: y + radius,
            r: radius,
            start: -Math.PI / 2,
        },
        { kind: 'line', len: straightH, from: [x + w, y + radius], to: [x + w, y + h - radius] },
        {
            kind: 'arc',
            len: cornerLen,
            cx: x + w - radius,
            cy: y + h - radius,
            r: radius,
            start: 0,
        },
        { kind: 'line', len: straightW, from: [x + w - radius, y + h], to: [x + radius, y + h] },
        {
            kind: 'arc',
            len: cornerLen,
            cx: x + radius,
            cy: y + h - radius,
            r: radius,
            start: Math.PI / 2,
        },
        { kind: 'line', len: straightH, from: [x, y + h - radius], to: [x, y + radius] },
        { kind: 'arc', len: cornerLen, cx: x + radius, cy: y + radius, r: radius, start: Math.PI },
    ];
    const perimeter = segments.reduce((sum, seg) => sum + seg.len, 0);

    const pointAt = (dist) => {
        let remaining = dist;
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            const isLast = i === segments.length - 1;
            if (remaining <= seg.len || isLast) {
                const frac = seg.len ? Math.min(1, Math.max(0, remaining / seg.len)) : 0;
                if (seg.kind === 'line') {
                    return {
                        x: seg.from[0] + (seg.to[0] - seg.from[0]) * frac,
                        y: seg.from[1] + (seg.to[1] - seg.from[1]) * frac,
                    };
                }
                const a = seg.start + (Math.PI / 2) * frac;
                return { x: seg.cx + seg.r * Math.cos(a), y: seg.cy + seg.r * Math.sin(a) };
            }
            remaining -= seg.len;
        }
        return { x, y };
    };

    const points = [];
    for (let i = 0; i <= count; i++) {
        const t = i / count;
        const p = pointAt(t * perimeter);
        points.push({ x: p.x, y: p.y, t });
    }
    return points;
};

const createInstance = (canvas) => {
    const ctx = canvas.getContext('2d');
    const ringWidthPx = getRingWidthPx(canvas);
    let width = 0;
    let height = 0;
    let ringPath = null;
    let ringPoints = null;
    let clipPath = null;

    const resize = () => {
        const rect = canvas.getBoundingClientRect();
        if (rect.width < ringWidthPx * 2 || rect.height < ringWidthPx * 2) {
            // Too small to draw a meaningful ring (or not laid out yet) --
            // leave the previous geometry in place rather than building a
            // degenerate path from a transient/incorrect measurement.
            return false;
        }
        const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
        width = rect.width;
        height = rect.height;
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const parentEl = canvas.parentElement;
        let cardRadius = parseFloat(getComputedStyle(canvas).borderRadius) || 0;
        if (!cardRadius && parentEl) {
            cardRadius =
                parseFloat(getComputedStyle(parentEl).borderTopLeftRadius) ||
                parseFloat(getComputedStyle(parentEl).borderRadius) ||
                0;
        }
        if (!cardRadius) {
            // Viewport fallback: 25px (desktop) or 20px (mobile)
            cardRadius = window.innerWidth >= 768 ? 25 : 20;
        }
        // x=0, y=0, r=cardRadius alone still isn't quite the card's true
        // corner: the canvas is `position:absolute; inset:0` inside the
        // article, and an absolutely positioned element's inset offsets are
        // measured from its containing block's PADDING edge, not its
        // border edge -- so with the article's own border (border-box
        // sizing), the canvas's own top-left sits a border-width further
        // right/down than the article's true corner does (measured in the
        // field: article at (40,224), canvas at (41,225), a 1px offset).
        // cardRadius (read via getComputedStyle, inherited from the
        // article) is correct as a RADIUS -- it's the canvas's own origin
        // that's shifted from the point that radius is really centered on.
        // Building the path from (0,0) draws a curve centered a pixel away
        // from the card's real corner center, leaving exactly the kind of
        // corner-only gap reported in the field. Measuring that shift here
        // and offsetting the path's own origin by it puts the drawn curve's
        // center back on the card's true, fixed corner center.
        // The path also needs the ARTICLE's own true border-box width/height
        // here, not the canvas's (padding-box) width/height -- shifting only
        // the origin while reusing the canvas's smaller box left the path's
        // far (right/bottom) edge short of the article's true far edge by
        // twice the border width, so the ring sat flush on the near
        // side but noticeably short of the true edge on the far side --
        // confirmed in the field as a visibly asymmetric ring (thinner on
        // one side than the other, since the CSS clip trims the flush side's
        // stroke in half but the short side's stroke isn't reaching the
        // clip boundary at all yet, so more of it stays unclipped/visible).
        const parentRect = canvas.parentElement.getBoundingClientRect();
        const originOffsetX = rect.left - parentRect.left;
        const originOffsetY = rect.top - parentRect.top;
        const cardWidth = parentRect.width;
        const cardHeight = parentRect.height;
        ringPath = roundRectPath(-originOffsetX, -originOffsetY, cardWidth, cardHeight, cardRadius);
        ringPoints = buildArcLengthPoints(
            -originOffsetX,
            -originOffsetY,
            cardWidth,
            cardHeight,
            cardRadius,
            RAY_SEGMENT_COUNT
        );
        // Belt-and-suspenders containment: the card's own CSS (border-radius
        // on the canvas itself, overflow:hidden on the article ancestor)
        // should already keep every drawn pixel inside the rounded card, but
        // clipping the 2D context itself removes any dependency on that
        // working identically across browsers/DPRs. This clip is
        // deliberately looser than the card's true curve (a fixed pad, not
        // tied to cardRadius/ringWidthPx) -- it only needs to never be the
        // TIGHTER of the two constraints, since the real CSS clip is always
        // the one actually shaping the visible edge; if this one were tight
        // and mismatched instead, it would reintroduce the exact
        // wrong-curve problem described above.
        const clipPad = ringWidthPx;
        clipPath = roundRectPath(
            -originOffsetX - clipPad,
            -originOffsetY - clipPad,
            cardWidth + clipPad * 2,
            cardHeight + clipPad * 2,
            cardRadius + clipPad
        );
        return true;
    };

    const draw = (angle) => {
        if (!ringPath && !resize()) {
            return;
        }
        ctx.clearRect(0, 0, width, height);
        ctx.save();
        ctx.clip(clipPath);
        ctx.lineWidth = ringWidthPx;

        ctx.strokeStyle = 'rgba(39, 39, 32, 0.55)';
        ctx.stroke(ringPath);

        const rotationT = angle / (Math.PI * 2);
        for (let i = 0; i < ringPoints.length - 1; i++) {
            const p0 = ringPoints[i];
            const p1 = ringPoints[i + 1];
            const c0 = colorAt(p0.t + rotationT);
            const c1 = colorAt(p1.t + rotationT);
            if (c0.a < 0.02 && c1.a < 0.02) {
                continue;
            }
            ctx.beginPath();
            ctx.moveTo(p0.x, p0.y);
            ctx.lineTo(p1.x, p1.y);
            // A gradient across each tiny segment (its two ends' own
            // interpolated colors), not one flat color per segment, so
            // adjacent segments' strokes blend into each other the same way
            // the single continuous conic gradient used to -- a flat color
            // per segment left a faint step at every segment boundary,
            // reading as an unintentionally "hard-edged"/unsmoothed ray.
            const segmentGradient = ctx.createLinearGradient(p0.x, p0.y, p1.x, p1.y);
            segmentGradient.addColorStop(0, `rgba(${c0.r}, ${c0.g}, ${c0.b}, ${c0.a})`);
            segmentGradient.addColorStop(1, `rgba(${c1.r}, ${c1.g}, ${c1.b}, ${c1.a})`);
            ctx.strokeStyle = segmentGradient;
            ctx.stroke();
        }
        ctx.restore();
    };

    const isNearViewport = () => {
        const rect = canvas.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
            return false;
        }
        return (
            rect.top < window.innerHeight + VIEWPORT_MARGIN_PX && rect.bottom > -VIEWPORT_MARGIN_PX
        );
    };

    return { canvas, resize, draw, isNearViewport };
};

export default function initRecommendedGlowCanvas() {
    // Two contexts share this markup/class (see
    // assets/src/scss/components/_event-card-recommended-glow.scss's own doc
    // comment for why -- Recommended Events deliberately has no glow ring at
    // all, product decision) -- both need managing here identically.
    const SELECTOR = [
        '.events-grid .event-card--recommended .event-card__glow-canvas',
        '.all-events-block .event-card--recommended .event-card__glow-canvas',
    ].join(', ');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const attached = new WeakSet();
    // let, not const -- attachAll() below reassigns this to a pruned copy on
    // every rescan (see its own doc comment); tick()'s closure still reads
    // the current binding correctly either way.
    let instances = [];

    const resizeObserver =
        typeof window.ResizeObserver === 'function'
            ? new ResizeObserver((entries) => {
                  entries.forEach((entry) => {
                      instances.find((inst) => inst.canvas === entry.target)?.resize();
                  });
              })
            : null;

    // ACF's ServerSideRender (the Upcoming Events block's own preview in the
    // block editor) doesn't have its real markup -- these canvases included
    // -- in the DOM yet at the moment $(document).ready() fires common.js's
    // init calls; it replaces a placeholder with the real subtree
    // asynchronously afterward, and again on every later attribute change.
    // A one-time querySelectorAll() here found zero canvases on that first
    // pass and, with nothing watching for more, silently never drew a ring
    // again for the rest of that editing session -- confirmed in the field
    // as "no glow at all" specifically in the block editor, with the exact
    // same markup/CSS working fine on the live front end (which has its
    // real HTML present from the first paint, no ServerSideRender
    // involved). attachAll() re-running on every DOM mutation, the same
    // pattern already used by initEventsPreviewScale()/
    // initRecommendedEventsReorder() for this exact ACF behavior, picks up
    // each new batch of canvases as they actually appear. Stale instances
    // left pointing at a replaced/detached canvas are harmless (isNearViewport()
    // reads an all-zero rect and skips drawing) -- same tolerance those two
    // modules' own doc comments already document.
    //
    // The block editor's own preview markup (these canvases included) lives
    // inside Gutenberg's iframed editor canvas, a separate document from the
    // one this script loads into -- watchPreviewRoot() (see its own doc
    // comment) resolves the right document to scan/observe instead of always
    // scanning the outer `document`, where these canvases never exist while
    // previewing inside the editor. Confirmed live as the reason the glow
    // never rendered in ANY block editor before this, front end unaffected.
    const attachAll = (root) => {
        // Prunes entries for canvases a since-replaced ACF re-render already
        // removed from the DOM -- confirmed as a real, unbounded leak, not
        // just untidy state: ResizeObserver keeps a strong reference to
        // every target it's observing for as long as that observation is
        // active (per spec), so without unobserve() here a detached
        // canvas -- and everything it's rooted to -- stays reachable and
        // un-garbage-collectable for the rest of the editing session. Every
        // attribute-driven re-render (a drag reorder, the admin panel's own
        // live-preview refresh, toggling "Recommended") adds one more,
        // permanently, and `tick()` below keeps iterating all of them
        // forever regardless (isNearViewport() on a detached node reads an
        // all-zero rect and just no-ops, which is why this was never
        // visibly broken -- only slowly, silently growing).
        instances = instances.filter((inst) => {
            if (inst.canvas.isConnected) {
                return true;
            }
            resizeObserver?.unobserve(inst.canvas);
            return false;
        });
        root.querySelectorAll(SELECTOR).forEach((canvas) => {
            if (attached.has(canvas)) {
                return;
            }
            attached.add(canvas);
            const inst = createInstance(canvas);
            instances.push(inst);
            resizeObserver?.observe(canvas);
            if (reducedMotion) {
                // A single frozen frame instead of a live tick loop --
                // reduced motion means no sweep at all, not a slowed-down one.
                inst.draw(Math.PI / 2);
            }
        });
    };

    watchPreviewRoot((root) => {
        attachAll(root);
        const observer = new MutationObserver(() => attachAll(root));
        observer.observe(root.body, { childList: true, subtree: true });
    });

    if (reducedMotion) {
        return;
    }

    const start = performance.now();

    const tick = () => {
        const elapsed = (performance.now() - start) % ROTATION_PERIOD_MS;
        const angle = (elapsed / ROTATION_PERIOD_MS) * Math.PI * 2;
        instances.forEach((inst) => {
            if (inst.isNearViewport()) {
                inst.draw(angle);
            }
        });
    };

    tick();
    window.setInterval(tick, REDRAW_INTERVAL_MS);
}
